import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { PlatformService } from './platform.service';
import { LoggingService } from './logging.service';

interface DtrumApi {
  enterAction?(name: string, type?: string, startTime?: number, sourceUrl?: string, sourceAction?: number): number;
  leaveAction?(actionId: number): void;
  reportError?(error: string | Error, parentActionId?: number): void;
  identifyUser?(userId: string): void;
  endSession?(): void;
  reportString?(key: string, value: string): void;
  reportValue?(key: string, value: number): void;
  addActionProperties?(
    actionId: number,
    javaLong?: Record<string, number> | null,
    date?: Record<string, Date> | null,
    shortString?: Record<string, string> | null,
    javaDouble?: Record<string, number> | null,
  ): void;
  sendSessionProperties?(
    javaLong?: Record<string, number> | null,
    date?: Record<string, Date> | null,
    shortString?: Record<string, string> | null,
    javaDouble?: Record<string, number> | null,
  ): void;
}

@Injectable({ providedIn: 'root' })
export class DynatraceService {
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);
  private readonly document = inject(DOCUMENT);
  private readonly environment = inject(ENVIRONMENT);

  /** dtrum methods already reported as missing, so each gap is logged only once. */
  private readonly missingMethods = new Set<keyof DtrumApi>();

  private getDtrum(): DtrumApi | undefined {
    return (window as Window & { dtrum?: DtrumApi }).dtrum;
  }

  /**
   * Invokes a dtrum method only when the runtime agent actually exposes it.
   * The injected RUM agent may provide a reduced API surface.
   */
  private safeCall<K extends keyof DtrumApi>(
    method: K,
    ...args: Parameters<NonNullable<DtrumApi[K]>>
  ): ReturnType<NonNullable<DtrumApi[K]>> | undefined {
    const api = this.getDtrum();
    const fn = api?.[method];
    if (typeof fn !== 'function') {
      if (api) {
        this.warnMissingMethod(method);
      }
      return undefined;
    }
    return (fn as (...fnArgs: unknown[]) => ReturnType<NonNullable<DtrumApi[K]>>).apply(api, args);
  }

  private warnMissingMethod(method: keyof DtrumApi): void {
    if (this.missingMethods.has(method)) {
      return;
    }
    this.missingMethods.add(method);
    this.loggingService.logWarning(`DynatraceService: dtrum.${String(method)} is not available on the loaded RUM agent`);
  }

  /**
   * Attaches a property to an action, falling back to `addActionProperties`
   * when the agent does not expose the legacy `reportString`/`reportValue` API.
   */
  private reportActionProperty(actionId: number | undefined, key: string, value: string | number): void {
    const api = this.getDtrum();

    if (typeof value === 'string') {
      if (typeof api?.reportString === 'function') {
        this.safeCall('reportString', key, value);
        return;
      }
      if (api) {
        this.warnMissingMethod('reportString');
      }
      if (actionId !== undefined) {
        this.safeCall('addActionProperties', actionId, null, null, { [key]: value }, null);
      }
      return;
    }

    if (typeof api?.reportValue === 'function') {
      this.safeCall('reportValue', key, value);
      return;
    }
    if (api) {
      this.warnMissingMethod('reportValue');
    }
    if (actionId !== undefined) {
      if (Number.isInteger(value)) {
        this.safeCall('addActionProperties', actionId, { [key]: value }, null, null, null);
      } else {
        this.safeCall('addActionProperties', actionId, null, null, null, { [key]: value });
      }
    }
  }

  private leaveAction(actionId: number | undefined): void {
    if (actionId === undefined) {
      return;
    }
    this.safeCall('leaveAction', actionId);
  }

  initialize(): void {
    if (this.platformService.isNativeMobile()) {
      this.loggingService.logTrace('DynatraceService: Native agent handles initialization');
      return;
    }

    this.injectRumScript();
    this.loggingService.logTrace('DynatraceService: Web mode — dtrum API will be used if injected');
  }

  private injectRumScript(): void {
    const config = this.environment.appSettings.dynatrace;
    if (!config?.enabled) {
      return;
    }

    if (this.getDtrum()) {
      this.loggingService.logTrace('DynatraceService: Agent already loaded via index.html');
      return;
    }

    const { scriptUrl } = config;
    if (!scriptUrl) {
      this.loggingService.logWarning('DynatraceService: Missing scriptUrl — RUM script not injected');
      return;
    }

    if (this.document.getElementById('dynatrace-rum-agent')) {
      return;
    }

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptUrl;
    script.crossOrigin = 'anonymous';
    script.id = 'dynatrace-rum-agent';

    this.document.head.appendChild(script);
  }

  trackPageView(pageName: string): void {
    const actionId = this.safeCall('enterAction', `Page View: ${pageName}`, 'page_view');
    this.leaveAction(actionId);
  }

  trackButtonClick(buttonName: string, context?: string): void {
    const actionId = this.safeCall('enterAction', `Button Click: ${buttonName}`, 'button_click');
    if (context) {
      this.reportActionProperty(actionId, 'context', context);
    }
    this.leaveAction(actionId);
  }

  trackLogin(userId?: string): void {
    const actionId = this.safeCall('enterAction', 'User Login', 'login');
    if (userId) {
      this.safeCall('identifyUser', userId);
    }
    this.leaveAction(actionId);
  }

  trackLogout(): void {
    const actionId = this.safeCall('enterAction', 'User Logout', 'logout');
    this.leaveAction(actionId);
    this.safeCall('endSession');
  }

  trackPaymentInitiated(amount: number): void {
    const actionId = this.safeCall('enterAction', 'Payment Initiated', 'payment');
    this.reportActionProperty(actionId, 'paymentAmount', amount);
    this.leaveAction(actionId);
  }

  trackPaymentCompleted(amount: number, confirmationNumber?: string): void {
    const actionId = this.safeCall('enterAction', 'Payment Completed', 'payment');
    this.reportActionProperty(actionId, 'paymentAmount', amount);
    if (confirmationNumber) {
      this.reportActionProperty(actionId, 'confirmationNumber', confirmationNumber);
    }
    this.leaveAction(actionId);
  }

  trackPaymentFailed(errorMessage: string): void {
    const actionId = this.safeCall('enterAction', 'Payment Failed', 'payment');
    this.reportActionProperty(actionId, 'errorMessage', errorMessage);
    this.leaveAction(actionId);
  }

  trackError(error: string | Error): void {
    this.safeCall('reportError', error);
  }

  trackCustomAction(actionName: string, properties?: Record<string, string | number>): void {
    const actionId = this.safeCall('enterAction', actionName, 'custom');
    if (properties) {
      for (const [key, value] of Object.entries(properties)) {
        this.reportActionProperty(actionId, key, value);
      }
    }
    this.leaveAction(actionId);
  }

  identifyUser(userId: string): void {
    this.safeCall('identifyUser', userId);
  }

  reportSessionProperties(properties: Record<string, string | number>): void {
    const numericProps: Record<string, number> = {};
    const stringProps: Record<string, string> = {};

    for (const [key, value] of Object.entries(properties)) {
      const snakeKey = this.toSnakeCase(key);
      if (typeof value === 'string' && value) {
        stringProps[snakeKey] = value;
      } else if (typeof value === 'number') {
        numericProps[snakeKey] = value;
      }
    }

    const hasNumeric = Object.keys(numericProps).length > 0;
    const hasStrings = Object.keys(stringProps).length > 0;

    this.safeCall(
      'sendSessionProperties',
      hasNumeric ? numericProps : null,
      null,
      hasStrings ? stringProps : null,
      null,
    );
  }

  private toSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, (char, index) => `${index > 0 ? '_' : ''}${char.toLowerCase()}`);
  }
}
